import { defineStore } from "pinia";
import { isNetworkUnavailable } from "@/composables/network/offlineMode";
import { materialRestClient } from "@/api";
import db from "@/db";
import { buildQueryset } from "@/utils/queryset";
import { logRequestFailure } from "@/utils/network";

export const useMaterialsStore = defineStore("materials", {
  state: () => ({
    materialList: [],
    totalMaterials: 0,
    materialIsLoading: false,
  }),
  actions: {
    async fetchMaterials() {
      if (isNetworkUnavailable()) return;

      this.materialIsLoading = true;
      try {
        const lastMaterial = await db.materials
          .orderBy("updatedAt")
          .reverse()
          .first();
        const filters = {};
        if (lastMaterial) {
          filters.updatedAt = new Date(lastMaterial.updatedAt);
        }
        try {
          const response = await materialRestClient.listAll(filters);
          await db.transaction("rw", db.materials, async () => {
            const formattedData = await Promise.all(
              response.results.map((i) => db.models.Material.formatData(i))
            );
            db.materials.bulkPut(formattedData);
          });
          return { success: true, error: null };
        } catch (error) {
          logRequestFailure("Error fetching materials:", error);
          return { success: false, error };
        }
      } finally {
        this.materialIsLoading = false;
      }
    },

    async filterMaterials({ queryAsObject, ordering, filterMapping }) {
      while (this.materialIsLoading)
        await new Promise((r) => setTimeout(r, 200));
      this.materialIsLoading = true;
      try {
        let keepFiltering = true;
        let retry = 0;
        let queryset, count;
        // INFO - B.L - transaction is not blocking even when awaited, we try to filter multiple time to wait for the full load to finish when pression resync
        while (keepFiltering && retry < 10) {
          const query = await buildQueryset(
            db.materials,
            queryAsObject,
            ordering,
            filterMapping
          );
          queryset = query.queryset;
          count = query.count;
          if (count) keepFiltering = false;
          retry++;
        }

        this.materialList = await queryset.toArray();
        this.totalMaterials = count;
      } finally {
        this.materialIsLoading = false;
      }
    },

    async updateMaterial(material) {
      const response = await materialRestClient.update(material);
      await db.models.Material.createOrUpdate(response);
      return response;
    },

    async createMaterial(material) {
      const response = await materialRestClient.create(material);
      await db.models.Material.createOrUpdate(response);
      this.totalMaterials += 1;
      return response;
    },

    async deleteMaterial(materialUuid) {
      await materialRestClient.destroy(materialUuid);
      await db.materials.delete(materialUuid);
      this.materialList = this.materialList.filter(
        (material) => material.uuid !== materialUuid
      );
      this.totalMaterials -= 1;
    },
  },
});
